import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.svg';
import { BrandBackdrop } from '../components/BrandBackdrop';
import { landAfterSignIn } from '../utils/signInLanding';

/**
 * `/auth/signed-in` — where a sign-in with no continue target goes next.
 *
 * Google sign-in returns here: Central's callback redirects the browser with
 * nothing in hand, so this page makes the landing decision the password form
 * and the workspace picker make inline — a Central admin goes on to the admin
 * console, everyone else to `/home`. See utils/signInLanding.ts.
 *
 * Both exits REPLACE this entry, so Back never returns to a page that would
 * only decide again. Anyone opening it directly is routed the same way; with
 * no session at all that is `/home`, which sends them to sign in.
 */
export function SignedInLandingPage() {
  const navigate = useNavigate();
  // Once per page: StrictMode re-runs effects in development.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void landAfterSignIn(navigate, { replace: true });
  }, [navigate]);

  return (
    <BrandBackdrop>
      <header className="flex justify-center px-4 pt-8 sm:pt-12">
        <img src={logo} alt="LevelUp" className="h-7 drop-shadow sm:h-9" />
      </header>
      <main className="flex flex-1 items-start justify-center px-4 pb-10 pt-6 sm:pt-8">
        <div className="w-full max-w-lg rounded-3xl bg-white p-8 text-center shadow-2xl shadow-black/40 ring-1 ring-black/5">
          <div role="status" className="flex flex-col items-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
            <p className="mt-4 text-gray-500">Signing you in…</p>
          </div>
        </div>
      </main>
    </BrandBackdrop>
  );
}
